// src/Pages/ModuleRecords/MarketAddRecord.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import RecordWizard from '../../component/reusable/formRecords/RecordWizards';
import { Field, Select as SharedSelect, SectionHead, SelectWithOther, CurrencyInput } from '../../component/reusable/formRecords/formFieldPrimitives';
import { useApplyScopeLock, useLocationOptions } from '../../component/Admin/shared/useLocationOptions';
import RegistryCustomFields from '../../component/Admin/FoundationalTables/FormRegistry/RegistryCustomFields';
import { RegistryFormScope, useRegistryLabel } from '../../component/Admin/FoundationalTables/FormRegistry/registryFieldOverrides';
import GpsCoordinateField from '../../component/shared/GpsCoordinateField';
import { marketsApi } from '../../api/marketsApi';
import { marketPricesApi } from '../../api/marketPricesApi';
import { borderCrossingsApi } from '../../api/borderCrossingsApi';
import { vetCertificatesApi } from '../../api/vetCertificatesApi';
import {
  useBorderRegistryReferenceOptions,
  useBorderVetCreateOptions,
  useMarketPriceReferenceOptions,
} from '../../component/MarketTrad/useMarketTradReferences';
import {
  BORDER_DIRECTION_OPTIONS,
  COMMODITY_FORM_OPTIONS,
  GRADE_QUALITY_OPTIONS,
  INSPECTION_STATUS_OPTIONS,
  MARKET_SCHEDULE_OPTIONS,
  MARKET_STATUS_OPTIONS,
  MARKET_TYPE_OPTIONS,
  PRICE_TYPE_OPTIONS,
  PRICE_UNIT_OPTIONS,
  SEASONAL_FLAG_OPTIONS,
  normalizeSeasonalFlagValue,
  seasonalFlagLabel,
} from '../../component/MarketTrad/marketTradOptions';
import { useAuth } from '../../context/AuthContext';
import ProposeMarketModal from '../../component/MarketTrad/ProposeMarketModal';
import { registryCentralApi } from '../../api/registryCentralApi';
import { PIPELINE_STAGES, WORKFLOW_RULES, getStatusPipelineTargets } from '../../component/shared/RowActionMenu';
import {
  formatOtherOptionValue,
  splitOtherOptionValue,
} from '../../component/shared/forms/otherFieldHelpers';

const MODULE_NAME = 'Markets & Trade Intelligence';

const ALL_SUB_MODULES = [
  { value: 'registry', label: 'Market Registry' },
  { value: 'prices',   label: 'Market Price' },
  { value: 'border',   label: 'Border Crossing Registry' },
  { value: 'vet',      label: 'Veterinary Certificates Registry' },
];

const R05_SUB_MODULES = ['prices'];

const VET_PURPOSES = ['Trade', 'Movement', 'Export', 'Slaughter', 'Research', 'Import', 'Transit'];
const VET_STATUS_OPTIONS = [
  { value: 'valid', label: 'Valid' },
  { value: 'expired', label: 'Expired' },
  { value: 'revoked', label: 'Revoked' },
];

const STEPS_BY_SUBMODULE = {
  registry: [
    { key: 'basic',  label: 'Basic Data' },
    { key: 'market', label: 'Market Data' },
    { key: 'review', label: 'Review' },
  ],
  prices: [
    { key: 'price',  label: 'Market Price Data' },
    { key: 'system', label: 'System Information' },
    { key: 'review', label: 'Review' },
  ],
  border: [
    { key: 'border', label: 'Border Crossing Data' },
    { key: 'review', label: 'Review' },
  ],
  vet: [
    { key: 'cert',   label: 'Certification Registration' },
    { key: 'review', label: 'Review' },
  ],
};

const TAB_ROUTE = {
  registry: '/markets-trade/market-trade',
  prices:   '/markets-trade/market-prices',
  border:   '/markets-trade/border-crossing-registry',
  vet:      '/markets-trade/veterinary-certificate-registry',
};

// Same edit-permission matrix as LivestockAddRecord's Population flow — applied
// uniformly across all four sub-modules so "Edit Record" behaves consistently
// with the Livestock Population action menu/flow.
const EDITABLE_STATUSES_BY_ROLE = {
  R01: ['draft', 'rejected'],
  R02: [],
  R03: [],
  R04: ['draft', 'rejected'],
  R05: ['draft', 'rejected'],
};

function parseError(error, fallback) {
  return error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback;
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoISO(days) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
}

function normalizeOption(option) {
  if (option === null || option === undefined) return null;
  if (typeof option === 'string' || typeof option === 'number') {
    const text = String(option).trim();
    return text ? { value: text, label: text } : null;
  }
  const value = String(option.value ?? option.id ?? option.code ?? option.label ?? option.name ?? '').trim();
  if (!value) return null;
  const label = String(option.label ?? option.name ?? option.title ?? value).trim() || value;
  return { value, label };
}

// ── Shared visual primitives (same flat/gray language as LivestockAddRecord) ──
const INP =
  'w-full border-0 rounded-lg px-4 py-3 text-sm text-gray-700 bg-[#F3F5F9] ' +
  'placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-400 focus:bg-white appearance-none';
const INP_LOCKED =
  'w-full border-0 rounded-lg px-4 py-3 text-sm text-gray-400 bg-gray-50 cursor-not-allowed appearance-none';

// Field / Select / SectionHead come from the shared primitives so edits made
// to this module's Standard forms in Form Registry show up here.
function Select({ placeholder = 'Select…', ...props }) {
  return <SharedSelect placeholder={placeholder} {...props} />;
}

function SectionDivider() {
  return <div className="h-px w-full bg-green-200" />;
}

function ReadonlyBox({ children }) {
  return (
    <div className="flex items-center min-h-[2.75rem] px-4 py-3 rounded-lg bg-gray-50 text-xs text-gray-400 italic">
      {children}
    </div>
  );
}

const capitalize = (value) => {
  const text = String(value ?? '');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// Draft → Submitted → Validated → Approved, showing where the record is now
// and — once a new Record Status is chosen — the steps that will run
// automatically on save (or the single Reject step).
function StatusPipeline({ current, target }) {
  const currentStatus = String(current || 'draft').toLowerCase();
  const currentIndex = currentStatus === 'rejected' ? 0 : PIPELINE_STAGES.indexOf(currentStatus);
  const willRun = new Set(target?.path ?? []);

  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Validation Pipeline</p>
      <div className="flex flex-wrap items-center gap-1.5">
        {PIPELINE_STAGES.map((stage, index) => {
          const done = currentStatus !== 'rejected' && index <= currentIndex;
          const pending = willRun.has(stage);
          const cls = pending
            ? 'bg-blue-50 text-blue-700 border-blue-300 border-dashed'
            : done
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-white text-gray-400 border-gray-200';
          return (
            <React.Fragment key={stage}>
              {index > 0 && <span className="text-gray-300">→</span>}
              <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${cls}`}>
                {capitalize(stage)}{index === currentIndex && currentStatus !== 'rejected' ? ' (current)' : ''}
              </span>
            </React.Fragment>
          );
        })}
        {(currentStatus === 'rejected' || willRun.has('rejected')) && (
          <>
            <span className="text-gray-300">·</span>
            <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${willRun.has('rejected') ? 'border-dashed border-red-300 bg-red-50 text-red-600' : 'border-red-200 bg-red-50 text-red-600'}`}>
              Rejected{currentStatus === 'rejected' ? ' (current)' : ''}
            </span>
          </>
        )}
      </div>
      <p className="mt-2 text-xs text-gray-500">
        {target
          ? (target.value === 'rejected'
            ? 'On save, the record is rejected and returned to the originating officer.'
            : `On save, these steps run automatically: ${target.path.map(capitalize).join(' → ')}.`)
          : 'No status change selected — saving keeps the current status.'}
      </p>
    </div>
  );
}

function ReviewRow({ label: pageLabel, value }) {
  const labelText = useRegistryLabel(pageLabel);
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
      <span className="text-xs font-semibold text-gray-500">{labelText}</span>
      <span className="text-sm text-gray-900 text-right">{value || value === 0 ? String(value) : '—'}</span>
    </div>
  );
}

function labelFor(options, value) {
  return options.map(normalizeOption).filter(Boolean).find((o) => o.value === value)?.label ?? value;
}

// Multi-select for option objects { value, label } — used by Vet Certificate's
// "Species / Commodity Covered" field, ported from UnifiedCreateModal.jsx.
function MultiSelectOptionsPicklist({ values = [], onChange, options = [], placeholder = 'Select options' }) {
  const [open, setOpen] = useState(false);
  const toggle = (val) => onChange(values.includes(val) ? values.filter((v) => v !== val) : [...values, val]);
  const selectedLabels = options.filter((o) => values.includes(o.value));

  return (
    <div className="relative" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }} tabIndex={-1}>
      <div onClick={() => setOpen((o) => !o)}
        className="w-full px-4 py-3 rounded-lg text-sm bg-[#F3F5F9] cursor-pointer flex justify-between items-center hover:bg-gray-200 transition">
        <span className={values.length ? 'text-gray-700' : 'text-gray-400'}>
          {values.length ? `${values.length} selected` : placeholder}
        </span>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <polyline points="6 9 12 15 18 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      {open && (
        <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-52 overflow-y-auto">
          {options.length === 0 && <p className="px-4 py-3 text-[13px] text-gray-400">No options available</p>}
          {options.map((opt) => {
            const selected = values.includes(opt.value);
            return (
              <div key={opt.value} onMouseDown={(e) => { e.preventDefault(); toggle(opt.value); }}
                className={`px-4 py-2.5 text-[13px] cursor-pointer flex items-center gap-2.5 transition ${selected ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-50'}`}>
                <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition ${selected ? 'bg-green-600 border-green-600' : 'border-gray-300'}`}>
                  {selected && (
                    <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <polyline points="4 12 9 17 20 7" />
                    </svg>
                  )}
                </div>
                {opt.label}
              </div>
            );
          })}
        </div>
      )}
      {selectedLabels.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selectedLabels.map((opt) => (
            <span key={opt.value} className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 border border-green-200 rounded-lg text-[12px] font-medium">
              {opt.label}
              <button type="button" onMouseDown={(e) => { e.preventDefault(); toggle(opt.value); }} className="ml-0.5 text-green-400 hover:text-green-700 transition">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

const EMPTY_REGISTRY = {
  marketName: '', marketType: MARKET_TYPE_OPTIONS[0]?.value ?? '',
  marketSchedule: MARKET_SCHEDULE_OPTIONS.find((o) => o.value === 'weekly')?.value ?? MARKET_SCHEDULE_OPTIONS[0]?.value ?? '',
  status: MARKET_STATUS_OPTIONS[0]?.value ?? '',
  state: '', lga: '',
  gpsCoordinates: '',
};

const EMPTY_PRICES = {
  market_id: '', price_date: todayISO(), species_uuid: '',
  commodity_form: COMMODITY_FORM_OPTIONS[0]?.value ?? '', price_ngn: '',
  price_unit: PRICE_UNIT_OPTIONS[0]?.value ?? '', price_type: PRICE_TYPE_OPTIONS[0]?.value ?? '',
  seasonal_flag: SEASONAL_FLAG_OPTIONS[0]?.value ?? '', volume_traded: '0', grade_quality: '', data_source: '',
  data_source_other: '',
};

const EMPTY_BORDER = {
  state: '', border_point_id: '', crossing_direction: '', species_uuid: '', head_count: '',
  origin_country: '', destination_country: '', movement_date: todayISO(),
  vet_certificate_ref: '', movement_permit_number: '', inspection_status: INSPECTION_STATUS_OPTIONS[0]?.value ?? '',
};

const EMPTY_VET = {
  certificate_number: '', issued_at: todayISO(), expires_at: '', issuing_officer_name: '',
  issuing_authority_uuid: '', origin_market_uuid: '', state: '',
  species_uuids: [], head_count: '', destination: '', purpose: '', status: '', border_crossing_uuid: '',
  reason_for_revocation: '', notes: '',
};

export default function MarketAddRecord({ viewOnly = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { uuid } = useParams();
  const isEditMode = Boolean(uuid);
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const roleId = user?.roleId ?? 'R06';
  const isR05 = roleId === 'R05';
  const isFieldUser = roleId === 'R04' || roleId === 'R05';

  const visibleSubModules = isR05 ? ALL_SUB_MODULES.filter((s) => R05_SUB_MODULES.includes(s.value)) : ALL_SUB_MODULES;

  const initialSubModule = visibleSubModules.some((s) => s.value === searchParams.get('subModule'))
    ? searchParams.get('subModule')
    : (isR05 ? 'prices' : 'registry');
  const [subModule, setSubModuleState] = useState(initialSubModule);
  const setSubModule = (value) => {
    setSubModuleState(value);
    setStep(0);
    setError(''); setSuccessMsg('');
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('subModule', value); return next; }, { replace: true });
  };

  const [step, setStep] = useState(0);
  const steps = STEPS_BY_SUBMODULE[subModule];

  // ── Per-sub-module form state ────────────────────────────────────────────
  const [registryForm, setRegistryForm] = useState(EMPTY_REGISTRY);
  const [pricesForm, setPricesForm]     = useState(EMPTY_PRICES);
  const [borderForm, setBorderForm]     = useState(EMPTY_BORDER);
  const [vetForm, setVetForm]           = useState(EMPTY_VET);

  // ── Workflow state (prices / border have draft→submit; registry/vet are single-create) ──
  const [pricesSavedRecord, setPricesSavedRecord] = useState(null);
  const [pricesStatus, setPricesStatus]           = useState('draft');
  // Edit page Record Status: '' keeps the current status; otherwise the
  // status chosen from getStatusPipelineTargets(), applied on save.
  const [priceTargetStatus, setPriceTargetStatus] = useState('');
  const [priceRejectNotes, setPriceRejectNotes]   = useState('');
  const [borderSavedRecord, setBorderSavedRecord] = useState(null);
  const [borderStatus, setBorderStatus]           = useState('draft');
  const [saving, setSaving]         = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  // Market Price "+ Propose" → Registry Central proposal.
  const [proposeMarketOpen, setProposeMarketOpen] = useState(false);
  const [proposeNotice, setProposeNotice] = useState('');
  // Opened from Registry Central / the Market Registry "to complete" list:
  // the entry's name and location seed the form; saving completes the entry.
  const registryEntry = !uuid ? (location.state?.registryEntry ?? null) : null;

  // ── Edit-mode state — same pattern as LivestockAddRecord: seed from the
  // row handed over via navigation state, falling back to a live GET ────────
  const [editRow, setEditRow] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(isEditMode);

  // eslint-disable-next-line no-use-before-define
  useEffect(() => {
    if (!isEditMode) return;
    const seedRow = location.state?.row;
    if (seedRow && (seedRow.uuid === uuid || seedRow.id === uuid)) {
      setEditRow(seedRow);
      setLoadingRecord(false);
      return;
    }
    const api = { registry: marketsApi, prices: marketPricesApi, border: borderCrossingsApi, vet: vetCertificatesApi }[subModule];
    if (!api) { setLoadingRecord(false); return; }
    setLoadingRecord(true);
    api.get(uuid)
      .then((row) => setEditRow(row))
      .catch((e) => setError(parseError(e, 'Failed to load record.')))
      .finally(() => setLoadingRecord(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, uuid, subModule]);

  const currentEditStatus = (editRow?.data_status ?? editRow?.status ?? 'draft').toLowerCase();
  const canEditContent = !isEditMode || (EDITABLE_STATUSES_BY_ROLE[roleId] ?? []).includes(currentEditStatus);
  const fieldsLocked = isEditMode && !canEditContent;
  const priceRefs = useMarketPriceReferenceOptions();
  const borderRefs = useBorderRegistryReferenceOptions();

  // ── Registry: pre-fill on load ───────────────────────────────────────────
  // eslint-disable-next-line no-use-before-define
  useEffect(() => {
    if (!isEditMode || !editRow || subModule !== 'registry') return;
    setRegistryForm({
      marketName: editRow.market_name ?? '',
      marketType: editRow.market_type ?? '',
      marketSchedule: editRow.market_schedule ?? '',
      status: editRow.status ?? '',
      state: editRow.state ?? '',
      lga: editRow.lga ?? '',
      gpsCoordinates: editRow.gps_coordinates ?? '',
    });
  }, [isEditMode, editRow, subModule]);

  // ── Prices: pre-fill on load ─────────────────────────────────────────────
  useEffect(() => {
    if (!isEditMode || !editRow || subModule !== 'prices') return;
    // eslint-disable-next-line no-use-before-define
    const { value: dataSourceValue, otherValue: dataSourceOther } = splitOtherOptionValue(
      editRow.data_source ?? editRow.dataSource ?? '',
      priceRefs.dataSourceOptions
    );
    setPricesForm({
      market_id: editRow.market_uuid ?? '',
      price_date: editRow.price_date ?? todayISO(),
      species_uuid: editRow.species_uuid ?? '',
      commodity_form: editRow.commodity_form ?? '',
      price_ngn: editRow.price_ngn != null ? String(editRow.price_ngn) : '',
      price_unit: editRow.price_unit ?? '',
      price_type: editRow.price_type ?? '',
      seasonal_flag: normalizeSeasonalFlagValue(editRow.seasonal_flag ?? editRow.seasonalFlag ?? ''),
      volume_traded: editRow.volume_traded != null ? String(editRow.volume_traded) : '0',
      grade_quality: editRow.grade_quality ?? '',
      data_source: dataSourceValue,
      data_source_other: dataSourceOther,
      gps_coordinates: editRow.gps_coordinates ?? '',
    });
    setPricesSavedRecord(editRow);
    setPricesStatus(editRow.data_status ?? 'draft');
  }, [isEditMode, editRow, priceRefs.dataSourceOptions, subModule]);

  // ── Border: pre-fill on load ─────────────────────────────────────────────
  useEffect(() => {
    if (!isEditMode || !editRow || subModule !== 'border') return;
    // eslint-disable-next-line no-use-before-define
    setBorderForm({
      state: editRow.state ?? '',
      border_point_id: editRow.border_point_id ?? '',
      crossing_direction: editRow.crossing_direction ?? '',
      species_uuid: editRow.species_uuid ?? '',
      head_count: editRow.head_count != null ? String(editRow.head_count) : '',
      origin_country: editRow.origin_country ?? '',
      destination_country: editRow.destination_country ?? '',
      movement_date: editRow.movement_date ?? todayISO(),
      vet_certificate_ref: editRow.vet_certificate_ref ?? '',
      movement_permit_number: editRow.movement_permit_number ?? '',
      inspection_status: editRow.inspection_status ?? '',
    });
    setBorderSavedRecord(editRow);
    setBorderStatus(editRow.data_status ?? 'draft');
  }, [isEditMode, editRow, subModule]);

  // ── Vet: pre-fill on load ────────────────────────────────────────────────
  useEffect(() => {
    if (!isEditMode || !editRow || subModule !== 'vet') return;
    setVetForm({
      certificate_number: editRow.certificate_number ?? '',
      issued_at: editRow.issued_at ?? todayISO(),
      expires_at: editRow.expires_at ?? '',
      issuing_officer_name: editRow.issuing_officer_name ?? '',
      issuing_authority_uuid: editRow.issuing_authority_uuid ?? '',
      origin_market_uuid: editRow.origin_market_uuid ?? '',
      state: editRow.state ?? '',
      species_uuids: editRow.species_uuids ?? [],
      head_count: editRow.head_count != null ? String(editRow.head_count) : '',
      destination: editRow.destination ?? '',
      purpose: editRow.purpose ?? '',
      status: editRow.status ?? '',
      border_crossing_uuid: editRow.border_crossing_uuid ?? '',
      reason_for_revocation: editRow.reason_for_revocation ?? '',
      notes: editRow.notes ?? '',
    });
  }, [isEditMode, editRow, subModule]);

  // ── Reference data (loaded unconditionally, same pattern as LivestockAddRecord) ──
  const { stateOptions: registryStateOptions, lgaOptions: registryLgaOptions, loadingLgas: registryLoadingLgas } = useLocationOptions(registryForm.state);
  const [marketOptions, setMarketOptions] = useState([]);
  useEffect(() => { setMarketOptions(priceRefs.marketOptions); }, [priceRefs.marketOptions]);
  const [vetCertOptionsForBorder, setVetCertOptionsForBorder] = useState([]);
  useEffect(() => { setVetCertOptionsForBorder(borderRefs.vetCertificateOptions); }, [borderRefs.vetCertificateOptions]);
  const { stateOptions: borderStateOptions } = useLocationOptions();
  const vetRefs = useBorderVetCreateOptions();
  const { stateOptions: vetStateOptions, loadingStates: vetLoadingStates } = useLocationOptions();
  useEffect(() => {
    if (!registryEntry) return;
    setRegistryForm((p) => ({
      ...p,
      marketName: registryEntry.name ?? p.marketName,
      state: registryEntry.state || p.state,
      lga: registryEntry.lga || p.lga,
      marketType: registryEntry.market_type || p.marketType,
      status: registryEntry.market_status || p.status,
      marketSchedule: registryEntry.market_schedule || p.marketSchedule,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registryEntry?.uuid]);
  // R04 / R05: the record's state (and R05's LGA) are their assigned area.
  const { lockedState, lockedLga } = useApplyScopeLock(registryForm, setRegistryForm);
  useApplyScopeLock(borderForm, setBorderForm, { lgaKey: null });
  useApplyScopeLock(vetForm, setVetForm, { lgaKey: null });
  const [crossingOptions, setCrossingOptions] = useState([]);
  useEffect(() => {
    borderCrossingsApi.listAll({ per_page: 100 })
      .then(({ items }) => {
        setCrossingOptions(items.filter((c) => c.uuid).map((c) => ({
          value: c.uuid,
          label: [c.crossing_point || 'Crossing', c.crossing_date, c.direction && `(${c.direction})`, c.state && `— ${c.state}`].filter(Boolean).join(' '),
        })));
      })
      .catch(() => setCrossingOptions([]));
  }, []);

  const setReg = (k) => (e) => {
    const value = e && e.target ? e.target.value : e;
    setRegistryForm((p) => (k === 'state' ? { ...p, state: value, lga: '' } : { ...p, [k]: value }));
  };
  const setPrice = (k) => (e) => setPricesForm((p) => {
    const value = e && e.target ? e.target.value : e;
    if (k === 'data_source') {
      return {
        ...p,
        data_source: value,
        data_source_other: value.toLowerCase() === 'other' ? p.data_source_other : '',
      };
    }
    return { ...p, [k]: value };
  });
  const setBorder = (k) => (e) => setBorderForm((p) => {
    const value = e && e.target ? e.target.value : e;
    if (k === 'data_source') {
      return {
        ...p,
        data_source: value,
        data_source_other: value.toLowerCase() === 'other' ? p.data_source_other : '',
      };
    }
    return { ...p, [k]: value };
  });
  const setVet = (k) => (e) => setVetForm((p) => ({ ...p, [k]: e.target.value }));

  const goToTab = () => navigate(TAB_ROUTE[subModule]);

  // ── Step validity ────────────────────────────────────────────────────────
  const maxPriceDate = todayISO();
  const minPriceDate = isR05 ? daysAgoISO(14) : '';
  const priceDateOutOfRange = pricesForm.price_date && (pricesForm.price_date > maxPriceDate || (isR05 && pricesForm.price_date < minPriceDate));
  // Split across the two data steps so step 1's "Next" isn't gated on a field
  // (data_source) that only appears on step 2 — canPersistPrices (used for the
  // final Save/Submit on Review) still requires both.
  const priceStep1Valid =
    !priceRefs.loading && !priceRefs.error && !priceDateOutOfRange &&
    !!pricesForm.market_id && !!pricesForm.species_uuid && Number(pricesForm.price_ngn) > 0 &&
    !!pricesForm.price_date && !!pricesForm.commodity_form && !!pricesForm.price_unit &&
    !!pricesForm.price_type && Number(pricesForm.volume_traded) >= 0;
  const priceStep2Valid = !priceRefs.loading && !!pricesForm.data_source &&
    (String(pricesForm.data_source).toLowerCase() !== 'other' || !!pricesForm.data_source_other.trim());
  const canPersistPrices = priceStep1Valid && priceStep2Valid;

  const borderDateOutOfRange = borderForm.movement_date && borderForm.movement_date > todayISO();
  const canPersistBorder =
    !borderRefs.loading && !borderRefs.error && !borderDateOutOfRange &&
    !!borderForm.border_point_id && !!borderForm.crossing_direction && !!borderForm.species_uuid &&
    Number(borderForm.head_count) > 0 && !!borderForm.origin_country.trim() && !!borderForm.destination_country.trim() &&
    !!borderForm.movement_date && !!borderForm.inspection_status;

  const vetDateError = vetForm.issued_at && vetForm.expires_at && vetForm.expires_at < vetForm.issued_at;
  const vetIsRevoked = vetForm.status === 'revoked';
  const canCreateVet =
    !vetRefs.loading && !vetRefs.error &&
    !!vetForm.certificate_number.trim() && !!vetForm.issued_at && !!vetForm.expires_at &&
    !!vetForm.issuing_officer_name.trim() && !!vetForm.issuing_authority_uuid && !!vetForm.origin_market_uuid &&
    !!vetForm.state && vetForm.species_uuids.length > 0 && Number(vetForm.head_count) > 0 &&
    !!vetForm.destination.trim() && !!vetForm.purpose && !!vetForm.status &&
    (!vetIsRevoked || !!vetForm.reason_for_revocation.trim()) && !vetDateError;

  const canCreateRegistry =
    !!registryForm.marketName.trim() && !!registryForm.marketType &&
    !!registryForm.status && !!registryForm.state && !!registryForm.lga;

  const stepValidBySubmodule = {
    registry: [Boolean(registryForm.state) && Boolean(registryForm.lga), canCreateRegistry, true],
    prices:   [priceStep1Valid, priceStep2Valid, true],
    border:   [canPersistBorder, true],
    vet:      [canCreateVet, true],
  };
  const stepValid = fieldsLocked ? true : stepValidBySubmodule[subModule][step];

  // ── Registry: Create or Update ────────────────────────────────────────────
  const handleRegistrySave = async () => {
    if (saving) return;
    setSaving(true); setError('');
    const payload = {
      market_name: registryForm.marketName.trim(),
      market_type: registryForm.marketType,
      market_schedule: registryForm.marketSchedule || null,
      status: registryForm.status,
      state: registryForm.state,
      lga: registryForm.lga,
      gps_coordinates: registryForm.gpsCoordinates.trim() || null,
    };
    try {
      if (isEditMode) {
        await marketsApi.update(uuid, payload);
      } else {
        const created = await marketsApi.create(payload);
        if (registryEntry?.uuid) await registryCentralApi.complete(registryEntry.uuid, created?.uuid ?? created?.id ?? '');
      }
      navigate(TAB_ROUTE.registry);
    } catch (e) {
      setError(parseError(e, isEditMode ? 'Failed to update market record.' : 'Failed to create market record.'));
      setSaving(false);
    }
  };

  // ── Prices: draft → submit ───────────────────────────────────────────────
  const buildPricesPayload = (dataStatus = 'draft') => ({
    market_uuid: pricesForm.market_id,
    market_id: pricesForm.market_id,
    species_uuid: pricesForm.species_uuid,
    price_ngn: Math.round(Number(pricesForm.price_ngn || 0)),
    price_date: pricesForm.price_date,
    commodity_form: pricesForm.commodity_form,
    price_unit: pricesForm.price_unit,
    price_type: pricesForm.price_type,
    seasonal_flag: pricesForm.seasonal_flag,
    volume_traded: Number(pricesForm.volume_traded || 0),
    grade_quality: pricesForm.grade_quality || null,
    data_source: formatOtherOptionValue(pricesForm.data_source, pricesForm.data_source_other, priceRefs.dataSourceOptions),
    data_status: dataStatus,
  });
  const persistPricesDraft = async () => {
    const payload = buildPricesPayload('draft');
    const record = pricesSavedRecord?.uuid
      ? await marketPricesApi.update(pricesSavedRecord.uuid, payload)
      : await marketPricesApi.create(payload);
    setPricesSavedRecord(record);
    setPricesStatus(record.data_status ?? 'draft');
    return record;
  };
  const handlePricesSaveDraft = async () => {
    if (!canPersistPrices || saving || submitting) return;
    setSaving(true); setError(''); setSuccessMsg('');
    try { await persistPricesDraft(); setSuccessMsg('Draft saved.'); }
    catch (e) { setError(parseError(e, 'Failed to save market price draft.')); }
    finally { setSaving(false); }
  };
  // ── Prices (edit): Record Status pipeline ────────────────────────────────
  const priceStatusTargets = isEditMode
    ? getStatusPipelineTargets(WORKFLOW_RULES.marketPrices, roleId, pricesStatus)
    : [];
  const priceTarget = priceStatusTargets.find((t) => t.value === priceTargetStatus) ?? null;
  const priceRejectMissing = priceTarget?.value === 'rejected' && !priceRejectNotes.trim();

  // Runs each backend step of the chosen pipeline in order; stops at the first
  // failure and reports which step it was (earlier steps stay applied).
  const runPricesPipeline = async (recordUuid) => {
    const STEP = {
      submitted: { run: () => marketPricesApi.submit(recordUuid), label: 'Submit for Review' },
      validated: { run: () => marketPricesApi.validate(recordUuid), label: 'Validate' },
      approved:  { run: () => marketPricesApi.approve(recordUuid), label: 'Approve' },
      rejected:  { run: () => marketPricesApi.reject(recordUuid, priceRejectNotes), label: 'Reject' },
    };
    for (const stage of priceTarget.path) {
      try {
        await STEP[stage].run();
        setPricesStatus(stage);
      } catch (e) {
        throw new Error(`${STEP[stage].label} failed — ${parseError(e, 'the backend refused this step.')}`);
      }
    }
  };

  const handlePricesEditSave = async () => {
    if (saving || submitting || priceRejectMissing) return;
    if (!fieldsLocked && !canPersistPrices) return;
    if (fieldsLocked && !priceTarget) return;
    setSaving(true); setError(''); setSuccessMsg('');
    try {
      const record = fieldsLocked ? pricesSavedRecord : await persistPricesDraft();
      if (priceTarget) {
        await runPricesPipeline(record?.uuid ?? uuid);
        navigate(TAB_ROUTE.prices);
        return;
      }
      setSuccessMsg('Changes saved.');
    } catch (e) {
      setError(e?.message && !e?.response ? e.message : parseError(e, 'Failed to save market price record.'));
    } finally {
      setSaving(false);
    }
  };

  const handlePricesSubmit = async () => {
    if (!canPersistPrices || saving || submitting) return;
    setSubmitting(true); setError(''); setSuccessMsg('');
    try {
      const draft = await persistPricesDraft();
      await marketPricesApi.submit(draft.uuid);
      navigate(TAB_ROUTE.prices);
    } catch (e) {
      setError(parseError(e, 'Failed to submit market price record.'));
      setSubmitting(false);
    }
  };

  // ── Border: draft → submit ───────────────────────────────────────────────
  const buildBorderPayload = (dataStatus = 'draft') => {
    const borderPointLabel = labelFor(borderRefs.borderPointOptions, borderForm.border_point_id) || borderForm.border_point_id || null;
    const originDestination = [borderForm.origin_country.trim(), borderForm.destination_country.trim()].filter(Boolean).join(' / ') || borderForm.state.trim() || null;
    return {
      state: borderForm.state || '',
      border_point_id: borderForm.border_point_id,
      border_point_label: borderPointLabel,
      borderPointLabel,
      crossing_point: borderForm.border_point_id,
      crossing_direction: borderForm.crossing_direction,
      direction: borderForm.crossing_direction,
      species_uuid: borderForm.species_uuid,
      head_count: Number(borderForm.head_count || 0),
      origin_country: borderForm.origin_country.trim() || null,
      destination_country: borderForm.destination_country.trim() || null,
      origin_destination: originDestination,
      originDestination,
      movement_date: borderForm.movement_date,
      vet_certificate_ref: borderForm.vet_certificate_ref || null,
      movement_permit_number: borderForm.movement_permit_number || null,
      inspection_status: borderForm.inspection_status,
      status: borderForm.inspection_status,
      data_status: dataStatus,
    };
  };
  const persistBorderDraft = async () => {
    const payload = buildBorderPayload('draft');
    const record = borderSavedRecord?.uuid
      ? await borderCrossingsApi.update(borderSavedRecord.uuid, payload)
      : await borderCrossingsApi.create(payload);
    setBorderSavedRecord(record);
    setBorderStatus(record.data_status ?? 'draft');
    return record;
  };
  const handleBorderSaveDraft = async () => {
    if (!canPersistBorder || saving || submitting) return;
    setSaving(true); setError(''); setSuccessMsg('');
    try { await persistBorderDraft(); setSuccessMsg('Draft saved.'); }
    catch (e) { setError(parseError(e, 'Failed to save border crossing draft.')); }
    finally { setSaving(false); }
  };
  const handleBorderSubmit = async () => {
    if (!canPersistBorder || saving || submitting) return;
    setSubmitting(true); setError(''); setSuccessMsg('');
    try {
      const draft = await persistBorderDraft();
      await borderCrossingsApi.submit(draft.uuid);
      navigate(TAB_ROUTE.border);
    } catch (e) {
      setError(parseError(e, 'Failed to submit border crossing record.'));
      setSubmitting(false);
    }
  };

  // ── Vet: Create or Update ─────────────────────────────────────────────────
  const handleVetSave = async () => {
    if (saving) return;
    setSaving(true); setError('');
    const payload = {
      certificate_number: vetForm.certificate_number.trim(),
      issued_at: vetForm.issued_at,
      expires_at: vetForm.expires_at,
      issuing_officer_name: vetForm.issuing_officer_name.trim(),
      issuing_authority_uuid: vetForm.issuing_authority_uuid,
      origin_market_uuid: vetForm.origin_market_uuid,
      state: vetForm.state,
      species_uuids: vetForm.species_uuids,
      head_count: Number(vetForm.head_count),
      destination: vetForm.destination.trim(),
      purpose: vetForm.purpose,
      status: vetForm.status,
      data_status: isEditMode ? (editRow?.data_status ?? 'draft') : 'draft',
      border_crossing_uuid: vetForm.border_crossing_uuid || null,
      reason_for_revocation: vetIsRevoked ? vetForm.reason_for_revocation.trim() : null,
      notes: vetForm.notes.trim() || null,
    };
    try {
      if (isEditMode) {
        await vetCertificatesApi.update(uuid, payload);
      } else {
        await vetCertificatesApi.create(payload);
      }
      navigate(TAB_ROUTE.vet);
    } catch (e) {
      setError(parseError(e, isEditMode ? 'Failed to update veterinary certificate record.' : 'Failed to create veterinary certificate record.'));
      setSaving(false);
    }
  };

  // ── "Select Module" block — shared header for every sub-module's first step ──
  function renderSelectModule() {
    return (
      <>
        <SectionHead title="Select Module" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Module" required>
            <Select value={MODULE_NAME} disabled className={INP_LOCKED} options={[{ value: MODULE_NAME, label: MODULE_NAME }]} />
          </Field>
          <Field label="Sub Module" required>
            <Select value={subModule} onChange={(e) => setSubModule(e.target.value)} options={visibleSubModules} disabled={isEditMode}
              className={isEditMode ? INP_LOCKED : INP} />
          </Field>
        </div>
        <SectionDivider />
      </>
    );
  }

  // ── Registry step content ────────────────────────────────────────────────
  function renderRegistryBasic() {
    return (
      <div className="space-y-5">
        <div className="space-y-4">{renderSelectModule()}
          <SectionHead title="Geospatial" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="State" required>
              <Select value={registryForm.state} onChange={setReg('state')} options={registryStateOptions} placeholder="Select state"
                disabled={Boolean(lockedState)} className={lockedState ? INP_LOCKED : INP} />
            </Field>
            <Field label="LGA" required>
              <Select value={registryForm.lga} onChange={setReg('lga')} options={registryLgaOptions} disabled={Boolean(lockedLga) || !registryForm.state || registryLoadingLgas}
                className={lockedLga ? INP_LOCKED : INP}
                placeholder={registryForm.state ? (registryLoadingLgas ? 'Loading LGAs…' : 'Select LGA') : 'Select state first'} />
            </Field>
          </div>
          <GpsCoordinateField
            label="GPS Coordinates"
            value={registryForm.gpsCoordinates}
            onChange={setReg('gpsCoordinates')}
            disabled={fieldsLocked}
          />
        </div>
      </div>
    );
  }
  function renderRegistryMarket() {
    return (
      <div className="space-y-4">
        <SectionHead title="Market Data" />
        <Field label="Market Name" required>
          <input value={registryForm.marketName} onChange={setReg('marketName')} placeholder="e.g. Ikeja Central Market" className={INP} />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Market Type" required>
            <Select value={registryForm.marketType} onChange={setReg('marketType')} options={MARKET_TYPE_OPTIONS} placeholder="Select market type" />
          </Field>
          <Field label="Status" required>
            <Select value={registryForm.status} onChange={setReg('status')} options={MARKET_STATUS_OPTIONS} placeholder="Select status" />
          </Field>
          <Field label="Market Schedule">
            <Select value={registryForm.marketSchedule} onChange={setReg('marketSchedule')} options={MARKET_SCHEDULE_OPTIONS} placeholder="Select schedule" />
          </Field>
        </div>
      </div>
    );
  }
  function renderRegistryReview() {
    return (
      <>
        <SectionHead title="Geospatial" />
        <ReviewRow label="State" value={registryForm.state} />
        <ReviewRow label="LGA" value={registryForm.lga} />
        <ReviewRow label="GPS Coordinates" value={registryForm.gpsCoordinates} />
        <SectionHead title="Market Data" />
        <ReviewRow label="Market Name" value={registryForm.marketName} />
        <ReviewRow label="Market Type" value={labelFor(MARKET_TYPE_OPTIONS, registryForm.marketType)} />
        <ReviewRow label="Status" value={labelFor(MARKET_STATUS_OPTIONS, registryForm.status)} />
        <ReviewRow label="Market Schedule" value={labelFor(MARKET_SCHEDULE_OPTIONS, registryForm.marketSchedule)} />
      </>
    );
  }

  // ── Prices step content ──────────────────────────────────────────────────
  function renderPricesData() {
    return (
      <div className="space-y-5">
        <div className="space-y-4">{renderSelectModule()}
          <SectionHead title="Market Price Data" />
          <Field label="Market" required hint="Select from registry. Not listed? + Propose sends it to Registry Central for validation.">
            <div className="flex gap-2">
              <div className="flex-1">
                <Select value={pricesForm.market_id} onChange={setPrice('market_id')} options={marketOptions}
                  placeholder={priceRefs.loading ? 'Loading markets…' : 'Select from registry…'} disabled={priceRefs.loading} />
              </div>
              <button type="button" onClick={() => setProposeMarketOpen(true)}
                className="shrink-0 rounded-lg border border-dashed border-green-300 bg-green-50 px-4 py-3 text-[12px] font-semibold text-green-700 hover:bg-green-100">
                + Propose
              </button>
            </div>
          </Field>
          {proposeNotice && (
            <div className="rounded-lg border border-green-100 bg-green-50 px-4 py-2.5 text-xs text-green-700">{proposeNotice}</div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Price Date" required hint={isR05 ? 'Must be today or within the last 14 days.' : 'Must be on or before today.'}>
              <input type="date" value={pricesForm.price_date} onChange={setPrice('price_date')} min={isR05 ? minPriceDate : undefined} max={maxPriceDate} className={INP} />
            </Field>
            <Field label="Species" required>
              <Select value={pricesForm.species_uuid} onChange={setPrice('species_uuid')} options={priceRefs.speciesOptions}
                placeholder={priceRefs.loading ? 'Loading species…' : 'Select species'} disabled={priceRefs.loading} />
            </Field>
            <Field label="Commodity Form" required>
              <Select value={pricesForm.commodity_form} onChange={setPrice('commodity_form')} options={COMMODITY_FORM_OPTIONS} />
            </Field>
            <Field label="Price (₦)" required>
              <CurrencyInput value={pricesForm.price_ngn} onChange={setPrice('price_ngn')} placeholder="0" className={INP} />
            </Field>
            <Field label="Price Unit" required>
              <Select value={pricesForm.price_unit} onChange={setPrice('price_unit')} options={PRICE_UNIT_OPTIONS} />
            </Field>
            <Field label="Price Type" required>
              <Select value={pricesForm.price_type} onChange={setPrice('price_type')} options={PRICE_TYPE_OPTIONS} />
            </Field>
            <Field label="Seasonal Flag" required hint="Is this price observation from a seasonal peak (festival, dry/wet season, etc.)?">
              <Select value={pricesForm.seasonal_flag} onChange={setPrice('seasonal_flag')} options={SEASONAL_FLAG_OPTIONS} />
            </Field>
          </div>
        </div>

        <SectionDivider />

        <div className="space-y-4">
          <SectionHead title="Capacity Check" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Volume Traded" required hint="Enter 0 if not observed.">
              <input type="number" step="1" value={pricesForm.volume_traded} onChange={setPrice('volume_traded')} placeholder="0" className={INP} />
            </Field>
            <Field label="Grade / Quality" hint="Optional.">
              <Select value={pricesForm.grade_quality} onChange={setPrice('grade_quality')} options={GRADE_QUALITY_OPTIONS} placeholder="Optional" />
            </Field>
          </div>
        </div>
      </div>
    );
  }
  function renderPricesSystem() {
    return (
      <div className="space-y-5">
        <div className="space-y-4">
          <SectionHead title="Data Source" />
          <SelectWithOther
            label="Data Source"
            required
            value={pricesForm.data_source}
            otherValue={pricesForm.data_source_other}
            onChange={(value) => setPricesForm((p) => ({
              ...p,
              data_source: value,
              data_source_other: String(value).toLowerCase() === 'other' ? p.data_source_other : '',
            }))}
            onOtherChange={(value) => setPricesForm((p) => ({ ...p, data_source_other: value }))}
            options={priceRefs.dataSourceOptions}
            placeholder={priceRefs.loading ? 'Loading sources…' : 'Select source'}
            otherPlaceholder="Describe the source"
            disabled={priceRefs.loading}
          />
        </div>
        <SectionDivider />
        <div className="space-y-4">
          <SectionHead title="System" />
          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Record Status"
              hint={priceStatusTargets.length > 0 ? 'Choose a new status to apply when you save on the Review step.' : undefined}
            >
              {isEditMode && priceStatusTargets.length > 0 ? (
                <Select
                  value={priceTargetStatus}
                  onChange={(e) => { setPriceTargetStatus(e.target.value); setPriceRejectNotes(''); }}
                  placeholder={`${capitalize(pricesStatus)} (current)`}
                  options={priceStatusTargets.map((t) => ({
                    value: t.value,
                    label: t.path.length > 1 ? `${t.label} (auto pipeline)` : t.label,
                  }))}
                />
              ) : (
                <ReadonlyBox>{isEditMode ? `${capitalize(pricesStatus)} (current)` : 'Draft (system-set)'}</ReadonlyBox>
              )}
            </Field>
          </div>

          {isEditMode && (
            <StatusPipeline current={pricesStatus} target={priceTarget} />
          )}

          {priceTarget?.value === 'rejected' && (
            <Field label="Rejection Reason" required hint="Shown to the officer who submitted the record">
              <textarea
                value={priceRejectNotes}
                onChange={(e) => setPriceRejectNotes(e.target.value)}
                rows={3}
                placeholder="Describe what must be corrected before resubmitting..."
                className="w-full rounded-lg px-4 py-3 text-sm text-gray-700 bg-[#F3F5F9] focus:outline-none focus:ring-2 focus:ring-red-400 resize-none placeholder-gray-400"
              />
            </Field>
          )}
        </div>
      </div>
    );
  }
  function renderPricesReview() {
    return (
      <>
        <SectionHead title="Market Price Data" />
        <ReviewRow label="Market" value={labelFor(marketOptions, pricesForm.market_id)} />
        <ReviewRow label="Price Date" value={pricesForm.price_date} />
        <ReviewRow label="Species" value={labelFor(priceRefs.speciesOptions, pricesForm.species_uuid)} />
        <ReviewRow label="Commodity Form" value={labelFor(COMMODITY_FORM_OPTIONS, pricesForm.commodity_form)} />
        <ReviewRow label="Price (₦)" value={pricesForm.price_ngn} />
        <ReviewRow label="Price Unit" value={labelFor(PRICE_UNIT_OPTIONS, pricesForm.price_unit)} />
        <ReviewRow label="Price Type" value={labelFor(PRICE_TYPE_OPTIONS, pricesForm.price_type)} />
        <ReviewRow label="Seasonal Flag" value={seasonalFlagLabel(pricesForm.seasonal_flag)} />
        <SectionHead title="Capacity Check" />
        <ReviewRow label="Volume Traded" value={pricesForm.volume_traded} />
        <ReviewRow label="Grade / Quality" value={labelFor(GRADE_QUALITY_OPTIONS, pricesForm.grade_quality)} />
        <SectionHead title="System" />
        <ReviewRow
          label="Data Source"
          value={formatOtherOptionValue(pricesForm.data_source, pricesForm.data_source_other, priceRefs.dataSourceOptions)}
        />
        {isEditMode && <ReviewRow label="Record Status" value={`${capitalize(pricesStatus)} (current)`} />}
        {priceTarget && (
          <>
            <ReviewRow label="New Status" value={priceTarget.label} />
            <ReviewRow label="Applied Automatically" value={priceTarget.path.map(capitalize).join(' → ')} />
            {priceTarget.value === 'rejected' && <ReviewRow label="Rejection Reason" value={priceRejectNotes} />}
          </>
        )}
      </>
    );
  }

  // ── Border step content ──────────────────────────────────────────────────
  function renderBorderData() {
    return (
      <div className="space-y-5">
        <div className="space-y-4">{renderSelectModule()}
          <SectionHead title="Border Crossing Data" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="State">
              <Select value={borderForm.state} onChange={setBorder('state')} options={borderStateOptions} placeholder="Select state"
                disabled={Boolean(lockedState)} className={lockedState ? INP_LOCKED : INP} />
            </Field>
            <Field label="Border Point" required hint="Select from registry.">
              <Select value={borderForm.border_point_id} onChange={setBorder('border_point_id')} options={borderRefs.borderPointOptions}
                placeholder={borderRefs.loading ? 'Loading border points…' : 'Select from registry…'} disabled={borderRefs.loading} />
            </Field>
            <Field label="Crossing Direction" required>
              <div className="flex flex-wrap gap-5 pt-1">
                {BORDER_DIRECTION_OPTIONS.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700">
                    <input
                      type="radio"
                      name="crossing_direction"
                      value={opt.value}
                      checked={borderForm.crossing_direction === opt.value}
                      onChange={setBorder('crossing_direction')}
                      disabled={fieldsLocked}
                      className="accent-green-600"
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Species / Livestock" required>
              <Select value={borderForm.species_uuid} onChange={setBorder('species_uuid')} options={borderRefs.speciesOptions}
                placeholder={borderRefs.loading ? 'Loading species…' : 'Select species'} disabled={borderRefs.loading} />
            </Field>
            <Field label="Head Count" required>
              <input type="number" step="1" value={borderForm.head_count} onChange={setBorder('head_count')} placeholder="0" className={INP} />
            </Field>
            <Field label="Origin Country" required hint="Country or region of origin.">
              <input value={borderForm.origin_country} onChange={setBorder('origin_country')} placeholder="e.g. Niger, Chad, Cameroon" className={INP} />
            </Field>
            <Field label="Destination Country" required hint="Country or region of destination.">
              <input value={borderForm.destination_country} onChange={setBorder('destination_country')} placeholder="e.g. Nigeria" className={INP} />
            </Field>
            <Field label="Movement Date" required>
              <input type="date" value={borderForm.movement_date} onChange={setBorder('movement_date')} max={todayISO()} className={INP} />
              {borderDateOutOfRange && <p className="mt-1 text-[11px] text-red-600">Movement date cannot be in the future.</p>}
            </Field>
            <Field label="Movement Permit Number">
              <input value={borderForm.movement_permit_number} onChange={setBorder('movement_permit_number')} placeholder="Optional permit number" className={INP} />
            </Field>
            <Field label="Inspection Status" required>
              <Select value={borderForm.inspection_status} onChange={setBorder('inspection_status')} options={INSPECTION_STATUS_OPTIONS} />
            </Field>
          </div>
          <Field label="Veterinary Certificate Reference" hint="Conditional — link an existing certificate.">
            <Select value={borderForm.vet_certificate_ref} onChange={setBorder('vet_certificate_ref')} options={vetCertOptionsForBorder}
              placeholder={borderRefs.loading ? 'Loading certificates…' : 'Select from registry…'} disabled={borderRefs.loading} />
          </Field>
        </div>

        <SectionDivider />

        <div className="space-y-4">
          <SectionHead title="System" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Record Status">
              <ReadonlyBox>{isEditMode ? `${borderStatus.charAt(0).toUpperCase()}${borderStatus.slice(1)} (current)` : 'Draft (system-set)'}</ReadonlyBox>
            </Field>
          </div>
        </div>

      </div>
    );
  }
  function renderBorderReview() {
    return (
      <>
        <SectionHead title="Border Crossing Data" />
        <ReviewRow label="State" value={borderForm.state} />
        <ReviewRow label="Border Point" value={labelFor(borderRefs.borderPointOptions, borderForm.border_point_id)} />
        <ReviewRow label="Crossing Direction" value={labelFor(BORDER_DIRECTION_OPTIONS, borderForm.crossing_direction)} />
        <ReviewRow label="Species / Livestock" value={labelFor(borderRefs.speciesOptions, borderForm.species_uuid)} />
        <ReviewRow label="Head Count" value={borderForm.head_count} />
        <ReviewRow label="Origin Country" value={borderForm.origin_country} />
        <ReviewRow label="Destination Country" value={borderForm.destination_country} />
        <ReviewRow label="Movement Date" value={borderForm.movement_date} />
        <ReviewRow label="Veterinary Certificate Reference" value={labelFor(vetCertOptionsForBorder, borderForm.vet_certificate_ref)} />
        <ReviewRow label="Movement Permit Number" value={borderForm.movement_permit_number} />
        <ReviewRow label="Inspection Status" value={labelFor(INSPECTION_STATUS_OPTIONS, borderForm.inspection_status)} />
      </>
    );
  }

  // ── Vet step content ──────────────────────────────────────────────────────
  function renderVetData() {
    return (
      <div className="space-y-5">
        <div className="space-y-4">{renderSelectModule()}
          <SectionHead title="Certification Registration" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Certificate Number" required hint="Must be unique — used as the primary lookup key">
              <input value={vetForm.certificate_number} onChange={setVet('certificate_number')} placeholder="e.g. VET-202408-0001" className={INP} />
            </Field>
            <Field label="Status" required>
              <Select value={vetForm.status} onChange={setVet('status')} options={VET_STATUS_OPTIONS} placeholder="Select status" />
            </Field>
            <Field label="Issued At" required>
              <input type="date" value={vetForm.issued_at} onChange={setVet('issued_at')} className={INP} />
            </Field>
            <Field label="Expires At" required>
              <input type="date" value={vetForm.expires_at} onChange={setVet('expires_at')} min={vetForm.issued_at || undefined} className={INP} />
              {vetDateError && <p className="mt-1 text-[11px] text-red-600">Expires At must be on or after Issued At.</p>}
            </Field>
            <Field label="Issuing Officer" required>
              <input value={vetForm.issuing_officer_name} onChange={setVet('issuing_officer_name')} placeholder="e.g. Dr. A. Bello" className={INP} />
            </Field>
            <Field label="Issuing Authority" required hint="The regulatory body authorising this certificate">
              <Select value={vetForm.issuing_authority_uuid} onChange={setVet('issuing_authority_uuid')} options={vetRefs.institutionOptions}
                placeholder={vetRefs.loading ? 'Loading…' : 'Select authority'} disabled={vetRefs.loading} />
            </Field>
            <Field label="Origin Market" required>
              <Select value={vetForm.origin_market_uuid} onChange={setVet('origin_market_uuid')} options={vetRefs.marketOptions}
                placeholder={vetRefs.loading ? 'Loading markets…' : 'Select market'} disabled={vetRefs.loading} />
            </Field>
            <Field label="State" required>
              <Select value={vetForm.state} onChange={setVet('state')} options={vetStateOptions}
                placeholder={vetLoadingStates ? 'Loading states…' : 'Select state'} disabled={vetLoadingStates || Boolean(lockedState)}
                className={lockedState ? INP_LOCKED : INP} />
            </Field>
            <Field label="Head Count / Quantity" required>
              <input type="number" step="1" value={vetForm.head_count} onChange={setVet('head_count')} placeholder="e.g. 100" className={INP} />
            </Field>
            <Field label="Purpose" required>
              <Select value={vetForm.purpose} onChange={setVet('purpose')} options={VET_PURPOSES} placeholder="Select purpose" />
            </Field>
          </div>
          <Field label="Destination" required>
            <input value={vetForm.destination} onChange={setVet('destination')} placeholder="e.g. Kano central abattoir" className={INP} />
          </Field>
          <Field label="Species / Commodity Covered" required hint="Select all species this certificate covers">
            <MultiSelectOptionsPicklist values={vetForm.species_uuids} onChange={(v) => setVetForm((p) => ({ ...p, species_uuids: v }))}
              options={vetRefs.speciesOptions} placeholder={vetRefs.loading ? 'Loading species…' : 'Select one or more species'} />
          </Field>
          <Field label="Linked Border Crossing Reference" hint="Optional — link to an existing crossing record">
            <Select value={vetForm.border_crossing_uuid} onChange={setVet('border_crossing_uuid')} options={crossingOptions} placeholder="Select crossing (optional)" />
          </Field>
          {vetIsRevoked && (
            <Field label="Reason for Revocation" required>
              <textarea value={vetForm.reason_for_revocation} onChange={setVet('reason_for_revocation')} rows={2}
                placeholder="State the reason this certificate is being revoked"
                className="w-full px-4 py-3 rounded-xl text-sm text-gray-600 bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400 resize-none placeholder-gray-400" />
            </Field>
          )}
          <Field label="Notes">
            <textarea value={vetForm.notes} onChange={setVet('notes')} rows={2} placeholder="Optional notes" className={`${INP} resize-none`} />
          </Field>
        </div>

        <SectionDivider />

        <div className="space-y-4">
          <SectionHead title="System" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Confidence Level"><ReadonlyBox>auto · system-set</ReadonlyBox></Field>
            <Field label="Record Status">
              <ReadonlyBox>{isEditMode ? `${currentEditStatus.charAt(0).toUpperCase()}${currentEditStatus.slice(1)} (current)` : 'Draft (system-set)'}</ReadonlyBox>
            </Field>
          </div>
        </div>
      </div>
    );
  }
  function renderVetReview() {
    return (
      <>
        <SectionHead title="Certification Registration" />
        <ReviewRow label="Certificate Number" value={vetForm.certificate_number} />
        <ReviewRow label="Status" value={labelFor(VET_STATUS_OPTIONS, vetForm.status)} />
        <ReviewRow label="Issued At" value={vetForm.issued_at} />
        <ReviewRow label="Expires At" value={vetForm.expires_at} />
        <ReviewRow label="Issuing Officer" value={vetForm.issuing_officer_name} />
        <ReviewRow label="Issuing Authority" value={labelFor(vetRefs.institutionOptions, vetForm.issuing_authority_uuid)} />
        <ReviewRow label="Origin Market" value={labelFor(vetRefs.marketOptions, vetForm.origin_market_uuid)} />
        <ReviewRow label="State" value={vetForm.state} />
        <ReviewRow label="Head Count / Quantity" value={vetForm.head_count} />
        <ReviewRow label="Purpose" value={vetForm.purpose} />
        <ReviewRow label="Destination" value={vetForm.destination} />
        <ReviewRow label="Species / Commodity Covered" value={vetForm.species_uuids.map((v) => labelFor(vetRefs.speciesOptions, v)).join(', ')} />
        {vetIsRevoked && <ReviewRow label="Reason for Revocation" value={vetForm.reason_for_revocation} />}
        <ReviewRow label="Notes" value={vetForm.notes} />
      </>
    );
  }

  const STEP_RENDERERS = {
    registry: [renderRegistryBasic, renderRegistryMarket, renderRegistryReview],
    prices:   [renderPricesData, renderPricesSystem, renderPricesReview],
    border:   [renderBorderData, renderBorderReview],
    vet:      [renderVetData, renderVetReview],
  };

  function renderReviewFooter() {
    if (subModule === 'registry') {
      return (
        <>
          <button type="button" onClick={() => setStep((s) => s - 1)} className="flex-1 h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">Previous</button>
          <button type="button" onClick={handleRegistrySave} disabled={saving || fieldsLocked} className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? (isEditMode ? 'Saving…' : 'Creating…') : (isEditMode ? 'Save Changes' : 'Create')}
          </button>
        </>
      );
    }
    if (subModule === 'prices' && isEditMode) {
      const saveLabel = priceTarget
        ? (fieldsLocked ? `Set to ${priceTarget.label}` : `Save & Set to ${priceTarget.label}`)
        : 'Save Changes';
      return (
        <>
          <button type="button" onClick={() => setStep((s) => s - 1)} className="flex-1 min-w-[100px] h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">Previous</button>
          <button
            type="button"
            onClick={handlePricesEditSave}
            disabled={saving || submitting || priceRejectMissing || (fieldsLocked ? !priceTarget : !canPersistPrices)}
            className="flex-1 min-w-[130px] h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : saveLabel}
          </button>
        </>
      );
    }
    if (subModule === 'prices') {
      return (
        <>
          <button type="button" onClick={() => setStep((s) => s - 1)} className="flex-1 min-w-[100px] h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">Previous</button>
          <button type="button" onClick={handlePricesSaveDraft} disabled={!canPersistPrices || saving || submitting || fieldsLocked}
            className="flex-1 min-w-[130px] h-11 rounded-full border border-gray-300 bg-gray-50 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? 'Saving…' : (isEditMode ? 'Save Changes' : 'Save as Draft')}
          </button>
          <button type="button" onClick={handlePricesSubmit} disabled={!canPersistPrices || saving || submitting || fieldsLocked}
            className="flex-1 min-w-[130px] h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed">
            {submitting ? 'Submitting…' : (isR05 ? 'Submit for Review' : 'Submit')}
          </button>
        </>
      );
    }
    if (subModule === 'border') {
      return (
        <>
          <button type="button" onClick={() => setStep((s) => s - 1)} className="flex-1 min-w-[100px] h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">Previous</button>
          <button type="button" onClick={handleBorderSaveDraft} disabled={!canPersistBorder || saving || submitting || fieldsLocked}
            className="flex-1 min-w-[130px] h-11 rounded-full border border-gray-300 bg-gray-50 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? 'Saving…' : (isEditMode ? 'Save Changes' : 'Save as Draft')}
          </button>
          <button type="button" onClick={handleBorderSubmit} disabled={!canPersistBorder || saving || submitting || fieldsLocked}
            className="flex-1 min-w-[130px] h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed">
            {submitting ? 'Submitting…' : (isFieldUser ? 'Submit for Review' : 'Submit')}
          </button>
        </>
      );
    }
    // vet
    return (
      <>
        <button type="button" onClick={() => setStep((s) => s - 1)} className="flex-1 h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">Previous</button>
        <button type="button" onClick={handleVetSave} disabled={saving || fieldsLocked} className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed">
          {saving ? (isEditMode ? 'Saving…' : 'Creating…') : (isEditMode ? 'Save Changes' : 'Create')}
        </button>
      </>
    );
  }

  const isLastStep = step === steps.length - 1;
  const currentStatus = subModule === 'prices' ? pricesStatus
    : subModule === 'border' ? borderStatus
    : subModule === 'vet' ? (isEditMode ? currentEditStatus : 'draft')
    : 'draft';

  const REVIEW_RENDERERS = {
    registry: renderRegistryReview,
    prices: renderPricesReview,
    border: renderBorderReview,
    vet: renderVetReview,
  };

  // ── View-only mode: Review summary + an Edit Record button for viewers
  // who are actually allowed to edit this record — mirrors LivestockAddRecord's
  // viewOnly branch. ──────────────────────────────────────────────────────
  if (viewOnly) {
    return (
      <div className="p-6 bg-white min-h-screen">
        {loadingRecord && (
          <div className="mb-4 max-w-4xl mx-auto rounded-lg border border-amber-100 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700">
            Loading record…
          </div>
        )}
        {error && (
          <div className="mb-4 max-w-4xl mx-auto rounded-lg border border-red-100 bg-red-50 px-4 py-2.5 text-xs text-red-700">
            {error}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 max-w-4xl mx-auto">
          <div className="flex items-start justify-between mb-6 gap-4">
            <div>
              <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Record Details</h2>
              <p className="text-sm text-gray-400 mt-1">Review the information for this record</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-gray-500">Status:</span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize bg-gray-100 text-gray-600">
                {currentStatus}
              </span>
            </div>
          </div>

          <div className="space-y-4">{REVIEW_RENDERERS[subModule]()}</div>

          <div className="flex gap-3 pt-6 mt-6 border-t border-gray-100">
            <button type="button" onClick={goToTab} className="flex-1 h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">
              Back
            </button>
            {canEditContent && (
              <button
                type="button"
                onClick={() => navigate(`/markets-trade/edit/${uuid}?subModule=${subModule}`, { state: { row: editRow } })}
                className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700"
              >
                Edit Record
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white min-h-screen">
      {loadingRecord && (
        <div className="mb-4 max-w-4xl mx-auto rounded-lg border border-amber-100 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700">
          Loading record…
        </div>
      )}

      {fieldsLocked && (
        <div className="mb-4 max-w-4xl mx-auto rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
          This record is <strong>{currentEditStatus}</strong> — field values are locked for your role.
        </div>
      )}

      {registryEntry && subModule === 'registry' && (
        <div className="mx-auto mb-4 max-w-4xl rounded-lg border border-blue-100 bg-blue-50 px-4 py-2.5 text-xs text-blue-800">
          Completing the Registry Central entry <strong>{registryEntry.name}</strong>. Fill in the remaining market details — saving makes the entry Active.
        </div>
      )}

      <ProposeMarketModal
        isOpen={proposeMarketOpen}
        onClose={() => setProposeMarketOpen(false)}
        onCreated={(entry) => setProposeNotice(
          entry?.status === 'pending_validation'
            ? `“${entry.name}” was sent to Registry Central for validation. You can select it once it is validated and completed in Market Registry.`
            : `“${entry?.name}” was added to Registry Central. Complete its details in Market Registry to make it selectable.`,
        )}
      />

      <RegistryFormScope moduleKey="markets-trade" submoduleKey={subModule}>
        <RecordWizard
          steps={steps}
          currentStep={step}
          title={isEditMode ? 'Edit Record' : 'Add New Record'}
          subtitle={isEditMode ? 'Update the details for this record' : 'Fill in the details to create a new record'}
          statusLabel={currentStatus}
          error={error || successMsg}
          onCancel={goToTab}
          onBack={() => setStep((s) => s - 1)}
          onNext={() => setStep((s) => s + 1)}
          canGoNext={stepValid}
          nextLabel="Next"
          footer={isLastStep ? <>{renderReviewFooter()}</> : undefined}
        >
          <RegistryCustomFields moduleKey="markets-trade" submoduleKey={subModule} />
          {STEP_RENDERERS[subModule][step]()}
        </RecordWizard>
      </RegistryFormScope>
    </div>
  );
}
